import { BitmapLayer } from "@deck.gl/layers";
import { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import REGL, { TextureImageData } from "regl";

import { Bounds, TerrainCanvasInfo } from "@nens/deck-gl";

import { getSelectedModelRaster } from "../../state/layerMenu";
import { getModelExtent } from "../../state/simulation";

import demFrag from "./terrainLayer/shaders/dem.frag";
import elevationFrag from "./terrainLayer/shaders/elevation.frag";
import mapFrag from "./terrainLayer/shaders/map.frag";
import terrainVert from "./terrainLayer/shaders/terrain.vert";
import { long2tile, tile2long } from "./terrainLayer/terrainMain";

let loading = false;
const useTerrainLayer = (terrainCanvasInfo: TerrainCanvasInfo | null) => {
  const [renderCanvas, setRenderCanvas] = useState<HTMLCanvasElement | null>(null);
  const [bounds, setBounds] = useState<Bounds | null>(null);

  const extent = useSelector(getModelExtent);
  const demActive = useSelector(getSelectedModelRaster) === "dem_file";

  useEffect(() => {
    const createDemCanvas = async () => {
      if (!terrainCanvasInfo || !extent || !demActive) return;

      loading = true;

      const { imageNS, imageDEM, terrainBounds, zoom } = terrainCanvasInfo;

      const canvas = document.createElement("canvas");
      const regl = REGL({ canvas, extensions: ["OES_texture_float"] });

      const width = imageNS.width;
      const height = imageNS.height;

      canvas.width = width;
      canvas.height = height;

      const defaultReglProps = {
        vert: terrainVert,

        attributes: {
          position: [-1, -1, 1, -1, 1, 1, -1, -1, 1, 1, -1, 1],
        },
        uniforms: {
          elevationScale: 1.0,
          resolution: [width, height],
        },
        viewport: {
          x: 0,
          y: 0,
          width: width,
          height: height,
        },
        count: 6,
      };

      // Elevation texture
      const tElevation = regl.texture({
        data: imageNS as unknown as TextureImageData,
        flipY: false,
      });
      // Elevation framebuffer
      const fboElevation = regl.framebuffer({
        width,
        height,
        colorType: "float",
      });

      // Calculate elevation
      regl({
        ...defaultReglProps,
        frag: elevationFrag,

        uniforms: {
          ...defaultReglProps.uniforms,
          tElevation: tElevation,
        },
        framebuffer: fboElevation,
      })();

      const tLong = Math.floor(long2tile(extent.coordinates[0][0], zoom));
      const long0 = tile2long(tLong - 1, zoom);
      const long1 = tile2long(tLong + 2, zoom);
      const pixelScale = (6371000 * (long1 - long0) * 2 * Math.PI) / 360 / width;

      // Framebuffer normal map
      const fboNormal = regl.framebuffer({
        width,
        height,
        colorType: "float",
      });
      // Calculate normal map.
      regl({
        ...defaultReglProps,
        frag: mapFrag,

        uniforms: {
          ...defaultReglProps.uniforms,
          tElevation: fboElevation,
          pixelScale: pixelScale,
        },
        framebuffer: fboNormal,
      })();

      // Dem texture
      const tDem = regl.texture({
        data: imageDEM as unknown as TextureImageData,
        flipY: false,
      });

      // Calculate Dem texture with normal map
      regl({
        ...defaultReglProps,
        frag: demFrag,

        uniforms: {
          ...defaultReglProps.uniforms,
          tNormal: fboNormal,
          tDem: tDem,
        },
        depth: {
          enable: false,
        },
      })();

      regl.destroy();

      // Apply bounds and new canvas
      setTimeout(() => {
        loading = false;
        setRenderCanvas(canvas);
        setBounds(terrainBounds as Bounds);
      });
    };

    // Doesnt display correct image if vmin is not included
    if (!loading) createDemCanvas();
  }, [demActive, terrainCanvasInfo]);

  return useMemo(
    () =>
      bounds
        ? new BitmapLayer({
            id: "hillshade-layer",
            image: renderCanvas,
            bounds,
            visible: !!demActive,
          })
        : [],
    [bounds, renderCanvas, demActive]
  );
};

export default useTerrainLayer;
